import { useSyncExternalStore } from 'react'

/**
 * A request to open the workbench dock on one of the agent's tabs.
 *
 * The dock's visibility, height and maximise state belong to the workbench
 * layout — the same machinery build mode uses — so this store deliberately owns
 * none of that. It exists only as a bridge: the pen icon lives in the right
 * sidebar and the agent's stream lives in a hook, while the thing that can
 * actually open the bottom panel is the page's layout coordinator. Both sides
 * post a request here and the page acts on it.
 */

export type DockTab =
  | 'settings'
  | 'config'
  | 'fit'
  | 'alerting'
  | 'backtest'
  | 'versions'

export const AGENT_DOCK_TABS: DockTab[] = [
  'settings',
  'config',
  'fit',
  'alerting',
  'backtest',
  'versions',
]

/* Settings leads, and is therefore where the dock opens: it is the only tab that
   means something with no model loaded, and a first tab you never land on is
   just a tab in an odd place. Opening a model's card from the pen icon still
   asks for `config` explicitly. */
export const DEFAULT_AGENT_DOCK_TAB: DockTab = 'settings'

interface OpenRequest {
  tab: DockTab
  /** Agent-initiated requests are honoured only while the dock is closed. */
  source: 'user' | 'agent'
  /** Bumped per request so repeat asks for the same tab still register. */
  nonce: number
}

let request: OpenRequest | null = null
const listeners = new Set<() => void>()
const emit = (): void => listeners.forEach((listener) => listener())
const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
const snapshot = (): OpenRequest | null => request

let nonce = 0

/** Ask the page to reveal the dock, on a given tab. */
export const requestDock = (tab: DockTab = DEFAULT_AGENT_DOCK_TAB): void => {
  request = { tab, source: 'user', nonce: ++nonce }
  emit()
}

/**
 * Reveal the dock because the agent started filling it in. Separate from
 * `requestDock` so the page can honour it only when the panel is closed — an
 * agent writing knobs should not yank you off the tab you are reading.
 */
export const requestDockForAgent = (): void => {
  request = { tab: DEFAULT_AGENT_DOCK_TAB, source: 'agent', nonce: ++nonce }
  emit()
}

/** The page consumed the request. */
export const clearDockRequest = (): void => {
  if (!request) return
  request = null
  emit()
}

export const useDockRequest = (): OpenRequest | null =>
  useSyncExternalStore(subscribe, snapshot, snapshot)
